import { useEffect, useMemo, useState } from 'react';

type TabData = {
  slug: string;
  domain: string;
  path: string;
};

const TABS: TabData[] = [
  {
    slug: 'vacation',
    domain: 'app.rippling.com',
    path: '/time-products/dashboard/time-off/summary',
  },
  {
    slug: 'roadmap',
    domain: 'linear.app',
    path: '/acme/team/PROD/projects/q4-2025-roadmap',
  },
  {
    slug: 'okrs',
    domain: 'docs.google.com',
    path: '/spreadsheets/d/1aZ7xK9mP3qLvN8rT4wY2cF6hJ5gE0sB/edit#gid=0',
  },
];

const TYPE = 90;
const HOLD = 500;
const FLASH = 900;
const RESOLVE = 380;
const HELD = 2400;
const RESET_PHASE = 250;

type Phase = 'typing' | 'typed' | 'flash' | 'resolving' | 'resolved' | 'reset';
type CycleState = { phase: Phase; typed: number; p: number; resolveP: number };

function cycleTotal(slug: string) {
  return slug.length * TYPE + HOLD + FLASH + RESOLVE + HELD + RESET_PHASE;
}

function useCycle(slug: string, offset: number): CycleState {
  const total = cycleTotal(slug);
  const reduced = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );
  const [t, setT] = useState(0);

  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    const start = performance.now() - offset;
    const tick = (now: number) => {
      setT((((now - start) % total) + total) % total);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [offset, total, reduced]);

  if (reduced) return { phase: 'resolved', typed: slug.length, p: 0, resolveP: 1 };

  const e1 = slug.length * TYPE;
  const e2 = e1 + HOLD;
  const e3 = e2 + FLASH;
  const e4 = e3 + RESOLVE;
  const e5 = e4 + HELD;
  if (t < e1) return { phase: 'typing', typed: Math.floor(t / TYPE), p: 0, resolveP: 0 };
  if (t < e2) return { phase: 'typed', typed: slug.length, p: 0, resolveP: 0 };
  if (t < e3) return { phase: 'flash', typed: slug.length, p: (t - e2) / FLASH, resolveP: 0 };
  if (t < e4) return { phase: 'resolving', typed: slug.length, p: 0, resolveP: (t - e3) / RESOLVE };
  if (t < e5) return { phase: 'resolved', typed: slug.length, p: 0, resolveP: 1 };
  return { phase: 'reset', typed: 0, p: 0, resolveP: 1 - (t - e5) / RESET_PHASE };
}

function Slug({ data, typed, withCaret }: { data: TabData; typed: number; withCaret: boolean }) {
  return (
    <span style={{ color: 'var(--color-ink)', fontWeight: 500, display: 'inline-flex', alignItems: 'center' }}>
      <span style={{ color: 'var(--color-ink-muted)' }}>go/</span>
      <span>{data.slug.slice(0, typed)}</span>
      {withCaret && (
        <span
          style={{
            display: 'inline-block',
            width: 1.5,
            height: 14,
            background: 'var(--accent-solid)',
            marginLeft: 2,
            animation: 'hcCaret 0.9s steps(2) infinite',
          }}
        />
      )}
    </span>
  );
}

function Url({ data }: { data: TabData }) {
  return (
    <span
      style={{
        color: 'var(--color-ink-soft)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        fontSize: 13,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <span style={{ color: 'var(--color-ink-faint)' }}>https://</span>
      <span>{data.domain}</span>
      <span style={{ color: 'var(--color-ink-faint)' }}>{data.path}</span>
    </span>
  );
}

type TabProps = {
  data: TabData;
  offset: number;
  x: number;
  y: number;
  rot: number;
  z: number;
};

function Tab({ data, offset, x, y, rot, z }: TabProps) {
  const s = useCycle(data.slug, offset);
  const showSlug = s.phase === 'typing' || s.phase === 'typed' || s.phase === 'flash';
  const slugOp =
    s.phase === 'resolving' ? 1 - s.resolveP
    : showSlug ? 1
    : s.phase === 'reset' ? s.resolveP
    : 0;
  const urlOp =
    s.phase === 'resolving' ? s.resolveP
    : s.phase === 'resolved' ? 1
    : s.phase === 'reset' ? s.resolveP
    : 0;

  const enterScale = s.phase === 'flash' && s.p < 0.15 ? 1 + (1 - s.p / 0.15) * 0.3 : 1;
  let enterOp = 0;
  if (s.phase === 'flash') {
    if (s.p < 0.1) enterOp = s.p / 0.1;
    else if (s.p > 0.85) enterOp = (1 - s.p) / 0.15;
    else enterOp = 1;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: y,
        left: x,
        transform: `rotate(${rot}deg)`,
        zIndex: z,
        width: '100%',
        maxWidth: 460,
        background: '#fff',
        borderRadius: 14,
        border: '1px solid var(--color-hairline)',
        boxShadow: '0 18px 50px -14px rgba(20,20,40,0.22), 0 2px 6px -2px rgba(20,20,40,0.06)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '10px 14px',
          background: 'var(--color-bg-alt)',
          borderBottom: '1px solid var(--color-hairline)',
        }}
      >
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#ff5f57' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#febc2e' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#28c840' }} />
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            flex: 1,
            background: 'var(--color-bg)',
            borderRadius: 999,
            padding: '10px 16px',
            fontFamily: 'var(--font-mono)',
            fontSize: 14,
            border: '1px solid var(--color-hairline)',
            height: 22,
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              opacity: slugOp,
            }}
          >
            <Slug data={data} typed={s.typed} withCaret={s.phase === 'typing' || s.phase === 'typed'} />
          </div>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              opacity: urlOp,
            }}
          >
            <Url data={data} />
          </div>
          <div
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: `translateY(-50%) scale(${enterScale})`,
              opacity: enterOp,
              background: 'var(--accent-solid)',
              color: '#fff',
              borderRadius: 6,
              padding: '2px 6px',
              fontSize: 11,
              fontWeight: 700,
              fontFamily: 'var(--font-body)',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 2px 8px color-mix(in oklch, var(--accent-solid) 53%, transparent)',
              pointerEvents: 'none',
            }}
          >
            ⏎
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HeroTabCascade() {
  return (
    <div style={{ position: 'relative', height: 360, width: '100%' }}>
      <style>{`@keyframes hcCaret { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
      <Tab data={TABS[0]} offset={0} x={0} y={0} rot={-3} z={3} />
      <Tab data={TABS[1]} offset={1100} x={28} y={108} rot={2} z={2} />
      <Tab data={TABS[2]} offset={2200} x={10} y={216} rot={-1} z={1} />
    </div>
  );
}
